import React, { useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'

import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'

import { getGuardianInfoForEpisode } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  getDbtTeensSchedulingAttendees,
  getDbtTeensSchedulingParticipantNames,
  getDbtTeensSchedulingSessionTypeMetadata,
  getNextDbtTeensSessionNumber,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/dbtTeensScheduling'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import NavButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/navButton/NavButton'
import PrimaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/primaryButton/PrimaryButton'
import SessionsList from '@lyrahealth-inc/ui-core/lib/lyraTherapy/sessionsList/SessionsList'
import BootstrapContainer from '@lyrahealth-inc/ui-core/lib/molecules/layouts/bootstrapContainer/BootstrapContainer'
import CardFullWidth from '@lyrahealth-inc/ui-core/lib/molecules/layouts/cardFullWidth/CardFullWidth'
import { DBTTeensSessionConfirmationCard } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/dbtTeensSessionConfirmationCard/DBTTeensSessionConfirmationCard'

import styles from './sessionSelectionConfirmation.module.scss'
import { mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { APPOINTMENT_OPERATIONS } from '../../common/constants/appConstants'
import {
  CLIENTS_NEW_SESSION_DETAILS,
  CLIENTS_RESCHEDULE_SESSION_DETAILS,
  CLIENTS_SESSIONS,
} from '../../common/constants/routingConstants'
import { getAuthUserId } from '../../data/auth/authSelectors'
import { getClientAppointmentDetails } from '../../data/lyraTherapy/clientSelectors'
import { RootState } from '../../data/store'
import { useAssignmentsData } from '../assignments/data/hooks/useAssignmentsData'
import {
  useCreateAppointmentMutation,
  useCreateRecurringAppointmentMutation,
  useUpdateAppointmentMutation,
} from '../clients/clientDetails/data/appointmentsApi'
import { clearSelectedAppointment, setAppointment } from '../clients/clientDetails/data/appointmentsSlice'
import { useAppointmentById } from '../clients/clientDetails/data/hooks/useAppointmentById'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../data/ltToastAutoActions'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

function SessionSelectionConfirmation({
  appointment: appointmentFromProps,
  operation,
  actions: { setToastContent, dispatch },
}: any) {
  const providerID: string | undefined = useSelector(getAuthUserId)
  const [isSchedulingAppointment, setIsSchedulingAppointment] = useState(false)
  const [createAppointment] = useCreateAppointmentMutation()
  const [createRepeatingAppointment] = useCreateRecurringAppointmentMutation()
  const [updateAppointment] = useUpdateAppointmentMutation()
  const navigate = useNavigate()
  const { appointmentId } = useParams<{ appointmentId: string }>()

  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const { isDBTTeensEnabled } = useFlags()
  const showDBTTeensConfirmation =
    operation === APPOINTMENT_OPERATIONS.NEW &&
    isDBTTeensEnabled &&
    selectedEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens
  const { data: clientDetails } = useSelectedClientData(undefined, { skip: !showDBTTeensConfirmation })
  const { data: clientAppointments = [] } = useClientAppointmentsData()
  const { data: assignments } = useAssignmentsData(undefined, { skip: !showDBTTeensConfirmation })

  // Fetch appointment details from API if appointmentId is in URL
  const { data: appointmentFromApi, isLoading: isLoadingAppointment } = useAppointmentById(
    appointmentId ? { appointmentId } : undefined,
  )

  // Use appointment from API if available, otherwise fall back to props
  const appointment = appointmentFromApi || appointmentFromProps

  // Set appointment in Redux when fetched from API
  useEffect(() => {
    if (appointmentFromApi && dispatch) {
      dispatch(setAppointment(appointmentFromApi))
    }
  }, [appointmentFromApi, dispatch])

  let headerCTA, submitText, goBack: any
  let onSubmit: () => Promise<Appointment | void>
  switch (operation) {
    case APPOINTMENT_OPERATIONS.NEW:
      headerCTA = 'Create new'
      submitText = 'Create session'
      goBack = () => navigate(CLIENTS_NEW_SESSION_DETAILS.route)
      const sessionCount = appointment?.repeatFor || 0
      const appointmentCreationFunction = sessionCount > 1 ? createRepeatingAppointment : createAppointment
      const appointmentsForEpisode = clientAppointments.filter((appt) => appt.episodeId === selectedEpisode?.id)
      const guardianInfo = getGuardianInfoForEpisode(appointmentsForEpisode, selectedEpisode?.participants)
      const appointmentWithRequiredFields = {
        ...appointment,
        episodeId: selectedEpisode?.id,
        episodeState: selectedEpisode?.state,
        episodeEndDate: selectedEpisode?.end_date,
        providerPatientId: selectedEpisode?.provider_patient_id,
        programId: selectedEpisode?.program_id,
        ...(guardianInfo.guardianLyraId ? guardianInfo : {}),
      }
      onSubmit = () => {
        return appointmentCreationFunction({
          providerId: providerID,
          data: {
            ...appointmentWithRequiredFields,
            appointment: appointmentWithRequiredFields,
            episodeId: selectedEpisode?.id,
            providerPatientId: selectedEpisode?.provider_patient_id,
            intervalDays: appointment?.sessionFrequency,
            sessionCount,
            programId: selectedEpisode?.program_id,
          },
        }).unwrap()
      }
      break
    case APPOINTMENT_OPERATIONS.RESCHEDULE:
      headerCTA = 'Reschedule'
      submitText = 'Reschedule'
      goBack = () => navigate(CLIENTS_RESCHEDULE_SESSION_DETAILS.route)
      onSubmit = () => {
        return updateAppointment({
          providerId: providerID,
          data: {
            ...appointment,
            programId: selectedEpisode?.program_id,
          },
        }).unwrap()
      }
      break
  }

  const submitAppointment = () => {
    setIsSchedulingAppointment(true)
    onSubmit()
      .then(() => {
        track({
          event: mixpanelEvents.CONFIRM_SCHEDULE_NEW_SESSION,
        })
        // Clear the appointment details after successful creation
        dispatch(clearSelectedAppointment())
        navigate(CLIENTS_SESSIONS.route)
        setToastContent({
          text: `Done! Successfully ${
            operation === APPOINTMENT_OPERATIONS.NEW ? 'scheduled' : 'rescheduled'
          } the appointment`,
          id: 'SessionSelectionConfirmation-Toast-Success',
          toastType: 'success',
        })
      })
      .catch(() => {
        setIsSchedulingAppointment(false)
      })
  }

  // Show loading state if fetching appointment from API
  if (appointmentId && isLoadingAppointment) {
    return (
      <BootstrapContainer col='col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2'>
        <div>Loading appointment details...</div>
      </BootstrapContainer>
    )
  }

  // Show error state if appointment is required but not available
  if (!appointment) {
    return (
      <BootstrapContainer col='col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2'>
        <div>
          <NavButton text='Back' styleType='back' onClick={goBack} />
          <div>Appointment details not found.</div>
        </div>
      </BootstrapContainer>
    )
  }

  const sessionNumber = getNextDbtTeensSessionNumber({
    appointments: clientAppointments,
    episodeId: selectedEpisode?.id,
  })
  const { teenName, caregiverName } = getDbtTeensSchedulingParticipantNames({
    client: clientDetails,
    episode: selectedEpisode,
    appointments: clientAppointments,
    assignments,
  })
  const attendeeNames = getDbtTeensSchedulingAttendees({
    sessionType: appointment.sessionType,
    teenName,
    caregiverName,
  }).map(({ name }) => name)
  const sessionTypeLabel = getDbtTeensSchedulingSessionTypeMetadata(appointment.sessionType)?.label

  return (
    <BootstrapContainer col='col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2'>
      <div>
        <NavButton text='Back' styleType='back' onClick={goBack} />
        <h2>{`${headerCTA} session`}</h2>
        <CardFullWidth className={styles['details-card']} roundCorners>
          <div className={styles.container}>
            {showDBTTeensConfirmation ? (
              <div className={styles['dbt-teens-confirmation-card']}>
                <DBTTeensSessionConfirmationCard
                  appointment={appointment}
                  attendeeNames={attendeeNames}
                  sessionNumber={sessionNumber}
                  sessionTypeLabel={sessionTypeLabel}
                />
              </div>
            ) : (
              <>
                {/* @ts-expect-error TS(2739): Type '{ userRole: string; appointments: any[]; }' ... Remove this comment to see the full error message */}
                <SessionsList userRole='provider' appointments={[appointment]} />
              </>
            )}
            <PrimaryButton
              type='submit'
              data-test-id='SessionSelectionConfirmation-submit-button'
              onClick={submitAppointment}
              isLoading={isSchedulingAppointment}
            >
              {submitText}
            </PrimaryButton>
          </div>
        </CardFullWidth>
      </div>
    </BootstrapContainer>
  )
}

SessionSelectionConfirmation.propTypes = {
  operation: PropTypes.oneOf([APPOINTMENT_OPERATIONS.NEW, APPOINTMENT_OPERATIONS.RESCHEDULE]),
}

const mapStateToProps = (state: RootState) => {
  return {
    appointment: getClientAppointmentDetails(state),
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: {
    ...bindActionCreators(
      {
        setToastContent,
      },
      dispatch,
    ),
    dispatch,
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(SessionSelectionConfirmation)
